import React, { createContext, useContext, useReducer, useEffect } from 'react';
import DashboardApi from '@app/api/dashboard-api/dashboard.actions';
import { loadGoogleMapsAPI } from '@app/utils/googleMapsLoader';
import { registerCacheInvalidation, registerDirectCacheOperations } from '@app/utils/cacheUtils';
import { setEncryptedItem, getEncryptedItem, removeEncryptedItem } from '@app/utils/encryptedStorage';
import UserSession from '@app/common/user-session';

interface MapDataState {
  // Google Maps API loading state
  googleMapsLoaded: boolean;
  googleMapsLoading: boolean;
  googleMapsError: string | null;
  
  // Dashboard data cache
  dashboardData: any | null;
  dashboardLoading: boolean;
  dashboardError: string | null;
  lastFetched: number | null;
  lastStatusCheck: number | null;
  
  // Cache initialization state
  cacheInitialized: boolean;
  
  // Derived data for maps
  sites: any[];
  controllers: any[];
}

interface MapDataContextType {
  state: MapDataState;
  loadGoogleMaps: () => Promise<void>;
  loadDashboardData: (force?: boolean) => Promise<void>;
  invalidateCache: () => void; // For full refresh when needed
  clearCache: () => void;
  
  // Direct cache CRUD operations (no API calls)
  addSiteToCache: (site: any) => void;
  removeSiteFromCache: (siteId: string) => void;
  addControllerToCache: (controller: any) => void;
  removeControllerFromCache: (controllerId: string) => void;
  updateSiteInCache: (site: any) => void;
  updateControllerInCache: (controller: any) => void;
}

const initialState: MapDataState = {
  googleMapsLoaded: false,
  googleMapsLoading: false,
  googleMapsError: null,
  
  dashboardData: null,
  dashboardLoading: false,
  dashboardError: null,
  lastFetched: null,
  lastStatusCheck: null,
  
  cacheInitialized: false,
  
  sites: [],
  controllers: [],
};

type MapDataAction = 
  | { type: 'GOOGLE_MAPS_LOADING' }
  | { type: 'GOOGLE_MAPS_SUCCESS' }
  | { type: 'GOOGLE_MAPS_ERROR'; payload: string }
  | { type: 'DASHBOARD_LOADING' }
  | { type: 'DASHBOARD_SUCCESS'; payload: { data: any; timestamp: number; allControllers?: any[] } }
  | { type: 'DASHBOARD_ERROR'; payload: string }
  | { type: 'STATUS_UPDATE_SUCCESS'; payload: { statusUpdates: any[]; timestamp: number } }
  | { type: 'ADD_SITE_TO_CACHE'; payload: any }
  | { type: 'REMOVE_SITE_FROM_CACHE'; payload: string }
  | { type: 'ADD_CONTROLLER_TO_CACHE'; payload: any }
  | { type: 'REMOVE_CONTROLLER_FROM_CACHE'; payload: string }
  | { type: 'UPDATE_SITE_IN_CACHE'; payload: any }
  | { type: 'UPDATE_CONTROLLER_IN_CACHE'; payload: any }
  | { type: 'CLEAR_CACHE' }
  | { type: 'CACHE_INITIALIZED' }
  | { type: 'HYDRATE_FROM_STORAGE'; payload: Partial<MapDataState> };

const mapDataReducer = (state: MapDataState, action: MapDataAction): MapDataState => {
  switch (action.type) {
    case 'GOOGLE_MAPS_LOADING':
      return {
        ...state,
        googleMapsLoading: true,
        googleMapsError: null,
      };
      
    case 'GOOGLE_MAPS_SUCCESS':
      return {
        ...state,
        googleMapsLoaded: true,
        googleMapsLoading: false,
        googleMapsError: null,
      };
      
    case 'GOOGLE_MAPS_ERROR':
      return {
        ...state,
        googleMapsLoading: false,
        googleMapsError: action.payload,
      };
      
    case 'DASHBOARD_LOADING':
      return {
        ...state,
        dashboardLoading: true,
        dashboardError: null,
      };
      
    case 'DASHBOARD_SUCCESS':
      const { data, timestamp, allControllers } = action.payload;
      const sites = data?.partner?.sites?.sites || [];
      
      // Merge dashboard controllers 
      const dashboardControllers = data?.partner?.controllers?.controllers || [];
      
      if (allControllers && allControllers.length > 0) {
        // Create maps 
        const dashboardControllersMap = new Map(
          dashboardControllers.map((c: any) => [c.id, c])
        );
        const allControllersMap = new Map(
          allControllers.map((c: any) => [c.id, c])
        );
        
        const mergedControllersMap = new Map();
        
        allControllers.forEach((controller: any) => {
          const dashboardController = dashboardControllersMap.get(controller.id);
          if (dashboardController) {

            mergedControllersMap.set(controller.id, dashboardController);
          } else {

            mergedControllersMap.set(controller.id, controller);
          }
        });
        
        dashboardControllers.forEach((controller: any) => {
          if (!allControllersMap.has(controller.id)) {
            mergedControllersMap.set(controller.id, controller);
          }
        });
        
        const controllers = Array.from(mergedControllersMap.values());
        
        return {
          ...state,
          dashboardData: data,
          dashboardLoading: false,
          dashboardError: null,
          lastFetched: timestamp,
          lastStatusCheck: timestamp, // Reset status check time
          sites,
          controllers,
        };
      } else {

        return {
          ...state,
          dashboardData: data,
          dashboardLoading: false,
          dashboardError: null,
          lastFetched: timestamp,
          lastStatusCheck: timestamp, // Reset status check time
          sites,
          controllers: dashboardControllers,
        };
      }
      
    case 'DASHBOARD_ERROR':
      return {
        ...state,
        dashboardLoading: false,
        dashboardError: action.payload,
      };

    case 'STATUS_UPDATE_SUCCESS':
      const { statusUpdates, timestamp: statusTimestamp } = action.payload;
      
      // Update sites with new status
      const updatedSites = state.sites.map(site => {
        const statusUpdate = statusUpdates.find(u => u.id === site.id && u.type === 'site');
        return statusUpdate ? { ...site, status: statusUpdate.status } : site;
      });
      
      // Update controllers with new status
      const updatedControllers = state.controllers.map(controller => {
        const statusUpdate = statusUpdates.find(u => u.id === controller.id && u.type === 'controller');
        return statusUpdate ? { ...controller, status: statusUpdate.status } : controller;
      });

      return {
        ...state,
        sites: updatedSites,
        controllers: updatedControllers,
        lastStatusCheck: statusTimestamp,
      };
      
    case 'ADD_SITE_TO_CACHE':
      return {
        ...state,
        sites: [...state.sites, action.payload],
      };
      
    case 'REMOVE_SITE_FROM_CACHE':
      return {
        ...state,
        sites: state.sites.filter(site => site.id !== action.payload),
      };
      
    case 'ADD_CONTROLLER_TO_CACHE':
      return {
        ...state,
        controllers: [...state.controllers, action.payload],
      };
      
    case 'REMOVE_CONTROLLER_FROM_CACHE':
      return {
        ...state,
        controllers: state.controllers.filter(controller => controller.id !== action.payload),
      };
      
    case 'UPDATE_SITE_IN_CACHE':
      return {
        ...state,
        sites: state.sites.map(site =>
          site.id === action.payload.id ? { ...site, ...action.payload } : site
        ),
      };
      
    case 'UPDATE_CONTROLLER_IN_CACHE':
      return {
        ...state,
        controllers: state.controllers.map(controller =>
          controller.id === action.payload.id ? { ...controller, ...action.payload } : controller
        ),
      };
      
    case 'CLEAR_CACHE':
      return {
        ...initialState,
        googleMapsLoaded: state.googleMapsLoaded, // Keep Google Maps loaded
      };
      
    case 'CACHE_INITIALIZED':
      return {
        ...state,
        cacheInitialized: true,
      };
      
    case 'HYDRATE_FROM_STORAGE':
      return {
        ...state,
        ...action.payload,
      };
      
    default:
      return state;
  }
};

const MapDataContext = createContext<MapDataContextType | null>(null);

export const useMapData = () => {
  const context = useContext(MapDataContext);
  if (!context) {
    throw new Error('useMapData must be used within a MapDataProvider');
  }
  return context;
};

// Cache duration: 4 hours for full data
const CACHE_DURATION = 4 * 60 * 60 * 1000;
// Status check interval: 30 seconds
const STATUS_CHECK_INTERVAL = 30 * 1000;
// Maximum age for status data before considering it stale: 2 minutes
const MAX_STATUS_AGE = 2 * 60 * 1000;
const STORAGE_KEY = 'mapDataCache';
const GOOGLE_MAPS_LOADED_KEY = 'googleMapsLoaded';

export const MapDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(mapDataReducer, initialState);

  // Helper function to check if user is logged out
  const isUserLoggedOut = (): boolean => {
    const partnerId = UserSession.getPartnerId();
    const token = UserSession.getToken();
    return !partnerId || !token;
  };

  // Check Google Maps status on mount
  useEffect(() => {
    const checkGoogleMapsLoaded = () => {
      // Check if Google Maps is already loaded in the current session
      const googleMaps = (window as any)?.google?.maps?.Map;
      
      if (googleMaps) {
        dispatch({ type: 'GOOGLE_MAPS_SUCCESS' });
        localStorage.setItem(GOOGLE_MAPS_LOADED_KEY, 'true');
        return;
      }

      // Check if Google Maps script is already in DOM (from early injection)
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (existingScript) {
        dispatch({ type: 'GOOGLE_MAPS_LOADING' });
        
        // Wait for the existing script to load
        const handleLoad = () => {
          dispatch({ type: 'GOOGLE_MAPS_SUCCESS' });
          localStorage.setItem(GOOGLE_MAPS_LOADED_KEY, 'true');
        };
        
        const handleError = () => {
          dispatch({ type: 'GOOGLE_MAPS_ERROR', payload: 'Failed to load Google Maps from early injection' });
        };
        
        existingScript.addEventListener('load', handleLoad);
        existingScript.addEventListener('error', handleError);
        
        // If already loaded by the time we check
        if ((window as any).google && (window as any).google.maps) {
          dispatch({ type: 'GOOGLE_MAPS_SUCCESS' });
          localStorage.setItem(GOOGLE_MAPS_LOADED_KEY, 'true');
        }
        
        return;
      }
    };

    checkGoogleMapsLoaded();
  }, []);

  // Hydrate from localStorage on mount
  useEffect(() => {
    const loadEncryptedData = async () => {
      const currentPath = window.location.pathname;
      const isOnAuthPage = currentPath.includes('/auth/') || 
                          currentPath.includes('/forgot-password') || 
                          currentPath.includes('/new-password') ||
                          currentPath.includes('/login');
      
      if (isOnAuthPage) {
        dispatch({ type: 'CACHE_INITIALIZED' });
        return;
      }

      try {
        const storedData = await getEncryptedItem(STORAGE_KEY);
        if (storedData) {
          const now = Date.now();
          
          // PARTNER CONTEXT VALIDATION: Check if cached data belongs to current partner
          const currentPartnerId = (() => {
            try {
              const userSession = JSON.parse(localStorage.getItem('et') || '{}');
              return userSession.partnerId;
            } catch {
              return null;
            }
          })();
          
          const cachePartnerId = storedData.partnerId;
          const isPartnerContextChanged = currentPartnerId !== cachePartnerId;
          
          if (isPartnerContextChanged) {
            if (process.env.NODE_ENV === 'development') {
              console.log(`Partner context changed (${cachePartnerId} → ${currentPartnerId}), clearing cache`);
            }
            // Clear cache for different partner and trigger fresh data load
            await removeEncryptedItem(STORAGE_KEY);
            await loadDashboardData(true); // Force fresh data for new partner
            return; // don't use cached data
          }
          
          // Check if cached data is still valid
          if (storedData.lastFetched && (now - storedData.lastFetched) < CACHE_DURATION) {
            dispatch({ type: 'HYDRATE_FROM_STORAGE', payload: storedData });
            if (process.env.NODE_ENV === 'development') {
              const ageMinutes = Math.floor((now - storedData.lastFetched) / 60000);
              console.log(`Hydrated from encrypted 4-hour cache (age: ${ageMinutes} minutes) for partner ${currentPartnerId}`);
            }
          } else {
            if (process.env.NODE_ENV === 'development') {
              const ageHours = storedData.lastFetched ? Math.floor((now - storedData.lastFetched) / 3600000) : 'unknown';
              console.log(`Encrypted 4-hour cache expired (age: ${ageHours} hours), clearing`);
            }
            await removeEncryptedItem(STORAGE_KEY);
          }
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.log('No cached data found, will fetch fresh data');
          }
        }
      } catch (error) {
        console.error('Error loading encrypted cache data', error);
        // Clear corrupted cache and ensure loading state is not stuck
        try {
          await removeEncryptedItem(STORAGE_KEY);
        } catch (clearError) {
          console.error('Failed to clear encrypted localStorage', clearError);
        }
        
        // If cache loading fails, ensure we trigger a fresh data load
        // This prevents the dashboard from being stuck in loading state
        if (process.env.NODE_ENV === 'development') {
          console.log('Cache loading failed, will trigger fresh data load');
        }
        
        // trigger the fresh data load
        try {
          const dashboardInfo = await DashboardApi.getInfo();
          const now = Date.now();
          
          dispatch({ 
            type: 'DASHBOARD_SUCCESS', 
            payload: { 
              data: dashboardInfo, 
              timestamp: now 
            } 
          });
          
          if (process.env.NODE_ENV === 'development') {
            console.log('Fresh data loaded after cache failure');
          }
        } catch (fetchError) {
          console.error('Failed to load fresh data after cache failure', fetchError);
          const errorMessage = fetchError instanceof Error ? fetchError.message : 'Failed to load dashboard data';
          dispatch({ type: 'DASHBOARD_ERROR', payload: errorMessage });
        }
      } finally {
        // Mark cache as initialized regardless of success/failure
        dispatch({ type: 'CACHE_INITIALIZED' });
        if (process.env.NODE_ENV === 'development') {
          console.log('Cache initialization complete');
        }
      }
    };

    loadEncryptedData();
  }, []);

  // Status polling with visibility support
  useEffect(() => {
    let statusInterval: NodeJS.Timeout;
    let isVisible = true;

    const checkStatusUpdates = async () => {
      if (!state.dashboardData) return; // Only check if we have cached data
      
      // Stop polling if user is logged out
      if (isUserLoggedOut()) {
        if (process.env.NODE_ENV === 'development') {
          console.log('User logged out - stopping status updates');
        }
        return;
      }

      try {
        if (process.env.NODE_ENV === 'development') {
          console.log('Checking status updates');
        }

        const statusUpdates = await DashboardApi.getStatusOnly();

        dispatch({
          type: 'STATUS_UPDATE_SUCCESS',
          payload: {
            statusUpdates,
            timestamp: Date.now()
          }
        });

        if (process.env.NODE_ENV === 'development') {
          console.log('Status updates applied');
        }
      } catch (error) {
        console.error('Error checking status updates', error);

      }
    };

    const handleVisibilityChange = () => {
      const wasHidden = !isVisible;
      isVisible = !document.hidden;

      if (process.env.NODE_ENV === 'development') {
        console.log(`Tab visibility changed: ${isVisible ? 'visible' : 'hidden'}`);
      }

      // If tab just became visible and we have stale data, refresh immediately
      if (isVisible && wasHidden && state.lastStatusCheck && !isUserLoggedOut()) {
        const timeSinceLastCheck = Date.now() - state.lastStatusCheck;
        const isStale = timeSinceLastCheck > MAX_STATUS_AGE;

        if (isStale) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`Status data is stale (${Math.floor(timeSinceLastCheck / 1000)}s old), refreshing immediately`);
          }
          // Immediately refresh status when tab becomes active after being stale
          checkStatusUpdates();
        }
      }
    };

    const handleWindowFocus = () => {
      // check for stale data on window focus, but only if user is still logged in
      if (state.lastStatusCheck && !isUserLoggedOut()) {
        const timeSinceLastCheck = Date.now() - state.lastStatusCheck;
        const isStale = timeSinceLastCheck > MAX_STATUS_AGE;

        if (isStale) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`Window focused with stale data (${Math.floor(timeSinceLastCheck / 1000)}s old), refreshing`);
          }
          checkStatusUpdates();
        }
      }
    };

    // Set up event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);

    // Start status polling if we have cached data and user is logged in
    if (state.dashboardData && !isUserLoggedOut()) {
      statusInterval = setInterval(checkStatusUpdates, STATUS_CHECK_INTERVAL);
      if (process.env.NODE_ENV === 'development') {
        console.log('Started 15-second status polling with visibility awareness');
      }
    }

    return () => {
      if (statusInterval) {
        clearInterval(statusInterval);
        if (process.env.NODE_ENV === 'development') {
          console.log('Stopped status polling');
        }
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [state.dashboardData]);

  // Persist to localStorage when data changes
  useEffect(() => {
    const persistEncryptedData = async () => {
      if (state.dashboardData && state.lastFetched) {
        try {
          // Get current partner ID to tag the cache
          const currentPartnerId = (() => {
            try {
              const userSession = JSON.parse(localStorage.getItem('et') || '{}');
              return userSession.partnerId;
            } catch {
              return null;
            }
          })();
          
          const dataToStore = {
            dashboardData: state.dashboardData,
            lastFetched: state.lastFetched,
            lastStatusCheck: state.lastStatusCheck,
            sites: state.sites,
            controllers: state.controllers,
            partnerId: currentPartnerId, // Tag cache with partner ID
          };
          
          await setEncryptedItem(STORAGE_KEY, dataToStore);
          
          if (process.env.NODE_ENV === 'development') {
            const serializedData = JSON.stringify(dataToStore);
            console.log(`Data encrypted and persisted securely for partner ${currentPartnerId}`, Math.round(serializedData.length / 1024), 'KB');
          }
        } catch (error) {
          console.warn('Failed to encrypt and persist to localStorage', error);
          // Clear corrupted or old data on storage errors
          try {
            await removeEncryptedItem(STORAGE_KEY);
          } catch (clearError) {
            console.error('Failed to clear encrypted localStorage', clearError);
          }
        }
      }
    };

    persistEncryptedData();
  }, [state.dashboardData, state.lastFetched, state.lastStatusCheck, state.sites, state.controllers]);

  const loadGoogleMaps = async (): Promise<void> => {
    // Check if Google Maps is already loaded in the current session
    const googleMaps = (window as any)?.google?.maps?.Map;
    
    if (googleMaps) {
      dispatch({ type: 'GOOGLE_MAPS_SUCCESS' });
      localStorage.setItem(GOOGLE_MAPS_LOADED_KEY, 'true');
      return;
    }

    // If already loaded or loading, don't reload
    if (state.googleMapsLoaded || state.googleMapsLoading) {
      return;
    }

    // Check if Google Maps script is already in DOM (from early injection)
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      dispatch({ type: 'GOOGLE_MAPS_LOADING' });
      
      // Wait for the existing script to load
      return new Promise((resolve, reject) => {
        const handleLoad = () => {
          dispatch({ type: 'GOOGLE_MAPS_SUCCESS' });
          localStorage.setItem(GOOGLE_MAPS_LOADED_KEY, 'true');
          resolve();
        };
        
        const handleError = () => {
          dispatch({ type: 'GOOGLE_MAPS_ERROR', payload: 'Failed to load Google Maps from early injection' });
          reject(new Error('Failed to load Google Maps from early injection'));
        };
        
        existingScript.addEventListener('load', handleLoad);
        existingScript.addEventListener('error', handleError);
        
        // If already loaded by the time we check
        if ((window as any).google && (window as any).google.maps) {
          dispatch({ type: 'GOOGLE_MAPS_SUCCESS' });
          localStorage.setItem(GOOGLE_MAPS_LOADED_KEY, 'true');
          resolve();
        }
      });
    }

    dispatch({ type: 'GOOGLE_MAPS_LOADING' });
    
    try {
      await loadGoogleMapsAPI();
      dispatch({ type: 'GOOGLE_MAPS_SUCCESS' });
      localStorage.setItem(GOOGLE_MAPS_LOADED_KEY, 'true');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load Google Maps';
      dispatch({ type: 'GOOGLE_MAPS_ERROR', payload: errorMessage });
      localStorage.removeItem(GOOGLE_MAPS_LOADED_KEY);
    }
  };

  const loadDashboardData = async (force: boolean = false): Promise<void> => {
    // Don't load data if user is logged out
    if (isUserLoggedOut()) {
      if (process.env.NODE_ENV === 'development') {
        console.log('User logged out - skipping dashboard data load');
      }
      return;
    }
    
    const now = Date.now();
    
    // Check if we should use cached data
    if (!force && state.dashboardData && state.lastFetched) {
      const timeSinceLastFetch = now - state.lastFetched;
      if (timeSinceLastFetch < CACHE_DURATION) {
        if (process.env.NODE_ENV === 'development') {
          const ageMinutes = Math.floor(timeSinceLastFetch / 60000);
          console.log(`Using 4-hour cached dashboard data (age: ${ageMinutes} minutes)`);
        }
        return;
      }
    }

    // Don't fetch if already loading
    if (state.dashboardLoading) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Dashboard already loading, skipping duplicate request');
      }
      return;
    }

    if (process.env.NODE_ENV === 'development') {
      const reason = force ? 'forced refresh' : 
                    !state.dashboardData ? 'no cached data' : 
                    !state.lastFetched ? 'no timestamp' : 
                    'cache expired';
      console.log(`Fetching fresh dashboard data (4-hour refresh) - Reason: ${reason}`);
    }

    dispatch({ type: 'DASHBOARD_LOADING' });
    
    try {
      const dashboardInfo = await DashboardApi.getInfo();
      
      // Fetch ALL controllers (including serverless) for complete cache
      const ControllerApi = await import('@app/api/controller/controller.actions');
      const allControllers = await ControllerApi.default.getAll() || [];
      
      dispatch({ 
        type: 'DASHBOARD_SUCCESS', 
        payload: { 
          data: dashboardInfo, 
          timestamp: now,
          allControllers: allControllers
        } 
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load dashboard data';
      dispatch({ type: 'DASHBOARD_ERROR', payload: errorMessage });
      console.error('Error loading dashboard data', error);
    }
  };

  // For full refresh when direct cache operations aren't sufficient
  const invalidateCache = (): void => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Cache invalidated - triggering refresh');
    }
    loadDashboardData(true); 
  };

  const clearCache = (): void => {
    dispatch({ type: 'CLEAR_CACHE' });
    // Clear encrypted cache asynchronously
    (async () => {
      try {
        await removeEncryptedItem(STORAGE_KEY);
      } catch (error) {
        console.error('Error clearing encrypted cache', error);
      }
    })();
    if (process.env.NODE_ENV === 'development') {
      console.log('Encrypted cache cleared');
    }
  };

  // Register cache operations for global access
  useEffect(() => {
    registerCacheInvalidation(invalidateCache);
    
    // Register direct cache operations for zero-cost CRUD
    registerDirectCacheOperations({
      addSiteToCache: (site: any) => dispatch({ type: 'ADD_SITE_TO_CACHE', payload: site }),
      removeSiteFromCache: (siteId: string) => dispatch({ type: 'REMOVE_SITE_FROM_CACHE', payload: siteId }),
      addControllerToCache: (controller: any) => dispatch({ type: 'ADD_CONTROLLER_TO_CACHE', payload: controller }),
      removeControllerFromCache: (controllerId: string) => dispatch({ type: 'REMOVE_CONTROLLER_FROM_CACHE', payload: controllerId }),
      updateSiteInCache: (site: any) => dispatch({ type: 'UPDATE_SITE_IN_CACHE', payload: site }),
      updateControllerInCache: (controller: any) => dispatch({ type: 'UPDATE_CONTROLLER_IN_CACHE', payload: controller }),
    });
  }, []);

  const contextValue: MapDataContextType = {
    state,
    loadGoogleMaps,
    loadDashboardData,
    invalidateCache,
    clearCache,
    
    // Direct cache CRUD operations (no API calls)
    addSiteToCache: (site: any) => dispatch({ type: 'ADD_SITE_TO_CACHE', payload: site }),
    removeSiteFromCache: (siteId: string) => dispatch({ type: 'REMOVE_SITE_FROM_CACHE', payload: siteId }),
    addControllerToCache: (controller: any) => dispatch({ type: 'ADD_CONTROLLER_TO_CACHE', payload: controller }),
    removeControllerFromCache: (controllerId: string) => dispatch({ type: 'REMOVE_CONTROLLER_FROM_CACHE', payload: controllerId }),
    updateSiteInCache: (site: any) => dispatch({ type: 'UPDATE_SITE_IN_CACHE', payload: site }),
    updateControllerInCache: (controller: any) => dispatch({ type: 'UPDATE_CONTROLLER_IN_CACHE', payload: controller }),
  };

  return (
    <MapDataContext.Provider value={contextValue}>
      {children}
    </MapDataContext.Provider>
  );
};

export default MapDataContext; 